// The verification view's footer: sticky at the foot of the Test pane, never scrolled past
// (TGCLOUD-1868, plan D4, D5).
//
//   "Not read from this report (N)"   what the reader did not read, its first lines shown and the
//                                     rest a click away; a line the report prints at a place is
//                                     shown there on request
//   "k of n parts confirmed"
//   "Nothing is saved until you save the Test."
//   the acknowledgement that the values were checked another way, while the report cannot be
//   shown (D5)
//   why Save waits, one sentence each, and the form's own Cancel and Save
//
// Save is disabled until every group is confirmed and every acknowledgement is made: the
// wrong-patient one, the reading warnings', and this one. The caller disables it (TestForm), from
// useReadingChecks.blockers; the footer says why. Before any reading is applied, Save also waits
// while whose part of a report about several people the Test is is still being chosen (`waiting`),
// and the footer says that too.
import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography
} from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { NOT_READ_KIND_LABELS } from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/labReportDisplay';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useReadingChecksContext } from './useReadingChecks';
import { useVerification } from './useVerificationView';
import {
  BEFORE_SAVING,
  NOTHING_SAVED_YET,
  SAVE_RECORDS_REPORT,
  SHOW_IN_REPORT,
  UNVERIFIABLE_ACKNOWLEDGEMENT,
  groupsConfirmedText,
  notReadHeadingText,
  showMoreText
} from './labReportVerificationVocabulary';

// The lines of "Not read from this report" shown before it is opened.
const FIRST_LINES = 2;

// "Before saving:" and why Save waits, one sentence each, each said in this language already
// (useReadingChecks.blockers, whichIndividual.choiceWaitText).
function BeforeSaving({ lines }) {
  const t = useAutoTranslation();
  return (
    <>
      <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
        {t(BEFORE_SAVING)}
      </Typography>
      {lines.map((b) => (
        <Typography
          key={b}
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary' }}
        >
          {b}
        </Typography>
      ))}
    </>
  );
}

// A note on what was not read: its kind in words, then the reading's own note (the report's, never
// translated). A kind this list does not know is shown as it is.
const noteText = (n, t) =>
  `${
    n?.kind && n.kind !== 'other'
      ? `${
          NOT_READ_KIND_LABELS[n.kind]
            ? t(NOT_READ_KIND_LABELS[n.kind])
            : n.kind
        }: `
      : ''
  }${n?.note || ''}`;

/**
 * @param {object} props
 * @param {object[]} [props.notRead]   the reading's notes on what was not read
 * @param {string[]} [props.waiting]   why Save waits while no reading is applied yet: whose part of
 *   a report about several people the Test is, still to choose (TGCLOUD-1868, Phase E4). One
 *   sentence each, said under "Before saving:"
 * @param {React.ReactNode} [props.error]  a refused save, said beside Save
 * @param {React.ReactNode} props.children  the form's buttons (ModalButtons), laid out in a Grid
 */
export default function VerificationFooter({
  notRead = [],
  waiting = [],
  error = null,
  children
}) {
  const t = useAutoTranslation();
  const checks = useReadingChecksContext();
  const view = useVerification();
  const [open, setOpen] = React.useState(false);
  const reading = !!checks && checks.active;
  const waits = (Array.isArray(waiting) ? waiting : []).filter(Boolean);
  // Everything Save waits for while a reading is applied, the choice of another report included.
  const blockers = reading ? [...checks.blockers, ...waits] : [];
  const notes = Array.isArray(notRead) ? notRead : [];
  const shown = open ? notes : notes.slice(0, FIRST_LINES);
  // A note the report prints at a place, while the report can be shown there.
  const placed = new Set(
    view && view.reportStatus !== 'failed'
      ? (view.anchors || [])
          .filter((a) => a && Array.isArray(a.rects) && a.rects.length)
          .map((a) => a.path)
      : []
  );

  return (
    <Box
      data-testid="verification-footer"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        mt: 2,
        pt: 1,
        pb: 1,
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}
    >
      {reading && (
        <Box sx={{ px: 1 }}>
          <Box data-testid="not-read" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {notReadHeadingText(notes.length, t)}
            </Typography>
            {/* Opened, a long list scrolls inside itself: the footer is sticky, and one taller than
                the pane could not be scrolled back to its own heading (review of the view). */}
            <Box
              data-testid="not-read-notes"
              sx={
                open
                  ? { maxHeight: '30vh', overflowY: 'auto', pr: 0.5 }
                  : undefined
              }
            >
              {shown.map((n, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}
                >
                  <Typography variant="body2" component="div">
                    {noteText(n, t)}
                  </Typography>
                  {placed.has(`notRead.${i}`) && (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<CropFreeIcon />}
                      aria-label={`${t(SHOW_IN_REPORT)}: ${noteText(n, t)}`}
                      onClick={() => view.select([`notRead.${i}`], 'value')}
                      sx={{ py: 0, minHeight: 0, textTransform: 'none' }}
                    >
                      {t(SHOW_IN_REPORT)}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
            {notes.length > FIRST_LINES && (
              <Button size="small" onClick={() => setOpen((v) => !v)}>
                {showMoreText(open, notes.length, t)}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 2,
              rowGap: 0.5,
              alignItems: 'baseline'
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700 }}
              data-testid="groups-confirmed"
            >
              {groupsConfirmedText(checks.count, checks.total, t)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(NOTHING_SAVED_YET)}
            </Typography>
            <Typography
              variant="body2"
              data-testid="save-records-report"
              sx={{ color: 'text.secondary' }}
            >
              {t(SAVE_RECORDS_REPORT)}
            </Typography>
          </Box>
          {view?.needsUnverifiableAcknowledgement && (
            <FormControlLabel
              data-testid="unverifiable-acknowledgement"
              sx={{ display: 'flex', mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={view.unverifiableAcknowledged === true}
                  onChange={(e) =>
                    view.setUnverifiableAcknowledged(e.target.checked)
                  }
                />
              }
              label={t(UNVERIFIABLE_ACKNOWLEDGEMENT)}
            />
          )}
          {blockers.length > 0 && (
            <Box data-testid="save-blockers" sx={{ mt: 0.5 }}>
              <BeforeSaving lines={blockers} />
            </Box>
          )}
        </Box>
      )}
      {!reading && waits.length > 0 && (
        <Box sx={{ px: 1 }} data-testid="save-blockers">
          <BeforeSaving lines={waits} />
        </Box>
      )}
      {error && <Box sx={{ px: 1, mt: 1 }}>{error}</Box>}
      <Grid container spacing={1} sx={{ mt: 0.5 }}>
        {children}
      </Grid>
    </Box>
  );
}
